import { useCallback, useEffect, useState } from 'react';
import { useLazyGetUsersListQuery } from '@src/services/users/usersList/usersListApiSlice';
import { useDeleteUserMutation } from '@src/services/users/deleteUser/deleteUserApiSlice';
import { User } from '@src/services/users/usersTypes';
import { actionType } from '../columns';
import _ from 'lodash';
import { errorHandler, useCheckResponse } from '@src/hooks';
import { useResendInvitationMutation } from '@src/services/users/addUsers/addUserApiSlice';
import { message } from 'antd';
import { FilterFormValue, normalizeFilters } from '@src/utilities/form';
import { SortDirection } from '@src/types/common';
import { LIST_LIMIT } from '@src/constants/pagination';
import { useSearchParams } from 'react-router-dom';

export function useUsersList(roleId?: string | null) {
    const [filters, setFilters] = useState<Record<string, FilterFormValue>>();
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [openFilter, setOpenFilter] = useState(false);
    const [openUserFormModal, setOpenUserFormModal] = useState(false);
    const [openUserDeleteModal, setOpenUserDeleteModal] = useState(false);
    const [openUserChangeRoleModal, setOpenUserChangeRoleModal] =
        useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get('page') || 1);
    const pageSize = Number(searchParams.get('pageSize') || LIST_LIMIT);
    const searchText = searchParams.get('search') || '';
    const sortBy = searchParams.get('sortBy') || null;
    const sortOrder = (searchParams.get('sortOrder') as SortDirection) || null;

    const { checkResponse } = useCheckResponse();
    const [getUsers, { data, isLoading, isFetching, isError }] =
        useLazyGetUsersListQuery();
    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
    const [resendInvitation] = useResendInvitationMutation();

    const fetchUsers = useCallback(
        _.debounce((params: any) => {
            getUsers(params)
                .unwrap()
                .catch((error) => {
                    checkResponse({ error });
                });
        }, 500),
        [getUsers, checkResponse]
    );

    useEffect(() => {
        const trimmedSearch = searchText?.trim();
        if (trimmedSearch && trimmedSearch.length < 3) return;

        fetchUsers({
            page,
            limit: pageSize,
            role: roleId || undefined,
            search: searchText || undefined,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
            ...normalizeFilters(filters),
        });
    }, [searchText, page, pageSize, filters, sortBy, sortOrder, roleId]);

    useEffect(() => {
        const filtersParam = searchParams.get('filters');
        if (filtersParam) {
            setFilters(JSON.parse(filtersParam));
        }
    }, []);

    const updateSearchParams = (
        params: Record<string, string | number | null | undefined>
    ) => {
        const newParams = {
            page,
            pageSize,
            search: searchText,
            sortBy,
            sortOrder,
            ...params,
        };

        Object.keys(newParams).forEach((key) => {
            if (
                newParams[key] === null ||
                newParams[key] === undefined ||
                newParams[key] === ''
            ) {
                searchParams.delete(key);
            } else {
                searchParams.set(key, String(newParams[key]));
            }
        });

        setSearchParams(searchParams);
    };

    const handleChangePagination = (newPage: number, newPageSize: number) => {
        updateSearchParams({ page: newPage, pageSize: newPageSize });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        updateSearchParams({ search: val, page: 1 });
    };

    const handleFilterChange = (key: string, value: FilterFormValue) => {
        setFilters((prev) => {
            const newFilters = { ...prev };

            if (
                value === 'ALL' ||
                (Array.isArray(value) && value.length === 0) ||
                value === '' ||
                value === null ||
                value === undefined
            ) {
                delete newFilters[key];
            } else {
                newFilters[key] = value;
            }

            const filtersParam = JSON.stringify(newFilters);
            if (Object.keys(newFilters).length === 0) {
                searchParams.delete('filters');
            } else {
                searchParams.set('filters', filtersParam);
            }
            searchParams.set('page', '1');
            setSearchParams(searchParams);

            return newFilters;
        });
    };

    const handleRemoveAllFilters = () => {
        setFilters({});

        searchParams.delete('filters');
        searchParams.set('page', '1');
        setSearchParams(searchParams);
    };

    const handleResendInvite = async (item: User) => {
        try {
            await resendInvitation({
                invitationId: item.id,
            }).unwrap();
            message.success({
                content: `Invitation resent to ${item.email}`,
                className: 'response-success',
                type: 'success',
            });
        } catch (error: any) {
            message.error({
                content: errorHandler(error),
                className: 'response-error',
                type: 'error',
            });
        }
    };
    const handleActionClick = (action: actionType, item: User) => {
        switch (action) {
            case 'delete':
                setSelectedUser(item);
                setOpenUserDeleteModal(true);
                break;
            case 'changeRole':
                setSelectedUser(item);
                setOpenUserChangeRoleModal(true);
                break;
            case 'resendInvite':
                handleResendInvite(item);
                break;
            case 'openFilter':
                setOpenFilter(true);
                break;
            default:
                console.warn('Unknown action:', action);
        }
    };

    const handleAddUser = () => {
        setSelectedUser(null);
        setOpenUserFormModal(true);
    };

    const confirmDelete = async () => {
        if (selectedUser) {
            await deleteUser({
                id: selectedUser.id,
            }).unwrap();
            setOpenUserDeleteModal(false);
        }
    };

    const handleSortChange = (
        columnKey: string,
        order: SortDirection | null
    ) => {
        updateSearchParams({ sortBy: columnKey, sortOrder: order, page: 1 });
    };

    return {
        page,
        pageSize,
        data,
        isLoading,
        isFetching,
        isDeleting,
        isError,
        selectedUser,
        searchText,
        filters,
        openFilter,
        openUserChangeRoleModal,
        openUserDeleteModal,
        openUserFormModal,
        sortBy,
        sortOrder,
        setFilters,
        setOpenFilter,
        setOpenUserChangeRoleModal,
        setOpenUserDeleteModal,
        setOpenUserFormModal,
        setSelectedUser,
        handleActionClick,
        handleAddUser,
        handleChangePagination,
        handleFilterChange,
        handleRemoveAllFilters,
        handleResendInvite,
        handleSearchChange,
        confirmDelete,
        handleSortChange,
    };
}
